import { AwaitContainer, ScrollArea, Sentinel, Spinner, useInfiniteScroll, useReverseScroll } from '@ibsys/kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateStopWord, useDeleteStopWord, useInfiniteStopWords, useUpdateStopWord } from '@/entities/stopWord';
import { StopWordItem } from './StopWordItem';
import { NEW_ID, StopWordFormValues, createStopWordsFormSchemaFactory } from './types';
import { useShiftEnter } from '@/shared/hooks';
import { scrollIntoViewWithinContainer } from '@/shared/lib';
import { PageContainer } from '@/shared/ui';

export const StopWordsPage: FC = () => {
  const { t } = useTranslation(['stopWords', 'common']);
  const bottomRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const shouldRestoreRef = useRef(false);
  const { captureScrollHeight, restoreScrollPosition } = useReverseScroll(viewportRef);
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteStopWords({
    limit: 10,
  });
  const createStopWord = useCreateStopWord();
  const updateStopWord = useUpdateStopWord();
  const deleteStopWord = useDeleteStopWord();
  const isRenderedFirstListRef = useRef(false);

  const schema = useMemo(() => createStopWordsFormSchemaFactory(t), [t]);

  // Transform API data to form values
  const defaultValues = useMemo<StopWordFormValues>(() => {
    const stopWords = data?.stopWords || [];

    // If list is empty, add one empty stop word
    if (stopWords.length === 0) {
      return {
        stopWords: [
          {
            id: NEW_ID,
            name: '',
            predicate: null,
          },
        ],
      };
    }

    return {
      stopWords: stopWords.map((stopWord) => ({
        id: stopWord.id,
        name: stopWord.name || '',
        predicate: stopWord.predicate || null,
      })),
    };
  }, [data?.stopWords]);

  const form = useForm<StopWordFormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    shouldFocusError: false,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'stopWords',
  });

  // Reset form when data changes
  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues.stopWords, defaultValues, form.reset]);

  useEffect(() => {
    if (!isRenderedFirstListRef.current) {
      scrollIntoViewWithinContainer(bottomRef.current);
      if (!isLoading) {
        isRenderedFirstListRef.current = true;
      }
    }
  }, [fields.length]);

  useLayoutEffect(() => {
    if (shouldRestoreRef.current) {
      restoreScrollPosition();
      shouldRestoreRef.current = false;
    }
  }, [fields]);

  const handleLoadMore = useCallback(() => {
    captureScrollHeight();
    shouldRestoreRef.current = true;
    fetchNextPage();
  }, [captureScrollHeight, fetchNextPage]);

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    threshold: 120,
    direction: 'reverse',
    root: viewportRef,
  });

  // Check if a specific stop word is valid
  const isStopWordValid = useCallback(
    (index: number) => {
      return form.trigger(`stopWords.${index}`);
    },
    [form.trigger],
  );

  // Check if a specific stop word is dirty
  const isStopWordDirty = useCallback(
    (index: number): boolean => {
      const fields = form.getValues(`stopWords.${index}`);

      const defaultFields = defaultValues.stopWords[index];

      return (
        !defaultFields ||
        fields.name !== defaultFields.name ||
        JSON.stringify(fields.predicate || {}) !== JSON.stringify(defaultFields.predicate || {})
      );
    },
    [form.getValues, defaultValues.stopWords],
  );

  const newStopWordIndex = useMemo(() => {
    return fields.findIndex((_field, index) => {
      const stopWord = form.getValues(`stopWords.${index}`);
      return stopWord?.id === NEW_ID;
    });
  }, [fields, form]);

  const newStopWord = form.getValues(`stopWords.${newStopWordIndex}`);

  // Handle save for a specific stop word
  const handleSave = useCallback(
    async (index: number) => {
      const stopWord = form.getValues(`stopWords.${index}`);
      const isValid = await isStopWordValid(index);

      if (!isValid) {
        return;
      }

      if (stopWord.id === NEW_ID) {
        // Create new stop word
        const response = await createStopWord.mutateAsync({
          name: stopWord.name,
          predicate:
            stopWord.predicate?.dsl && stopWord.predicate?.dslHuman
              ? {
                  dsl: stopWord.predicate.dsl!,
                  dslHuman: stopWord.predicate.dslHuman!,
                }
              : null,
        });

        // Update the stop word in form with new ID
        form.resetField(`stopWords.${index}`, { defaultValue: { ...stopWord, id: response.id }, keepDirty: false });
      } else {
        // Update existing stop word
        await updateStopWord.mutateAsync({
          id: stopWord.id!,
          payload: {
            name: stopWord.name,
            predicate:
              stopWord.predicate?.dsl && stopWord.predicate?.dslHuman
                ? {
                    dsl: stopWord.predicate.dsl!,
                    dslHuman: stopWord.predicate.dslHuman!,
                  }
                : null,
          },
        });

        // Reset dirty state for this stop word
        form.resetField(`stopWords.${index}`, { defaultValue: stopWord, keepDirty: false });
      }
    },
    [form, createStopWord, updateStopWord, isStopWordValid],
  );

  // Handle add new stop word
  const handleAdd = useCallback(() => {
    if (newStopWord) {
      handleSave(newStopWordIndex);
    }

    append({
      id: NEW_ID,
      name: '',
      predicate: null,
    });
  }, [append, newStopWord]);

  // Handle remove stop word
  const handleRemove = useCallback(
    async (index: number) => {
      const stopWord = form.getValues(`stopWords.${index}`);

      if (stopWord.id === NEW_ID) {
        // Just remove from form if it's new
        remove(index);
      } else {
        // Delete from API if it exists
        await deleteStopWord.mutateAsync(stopWord.id!);
        remove(index);
      }
    },
    [form, deleteStopWord, remove],
  );

  // Handle Shift+Enter for save and add
  const handleShiftEnter = useCallback(async () => {
    handleAdd();
    scrollIntoViewWithinContainer(bottomRef.current);
  }, [handleAdd, newStopWord]);

  const allowAddStopWord = !newStopWord || !!newStopWord.name;

  // Enable Shift+Enter hook
  useShiftEnter(handleShiftEnter, allowAddStopWord);

  return (
    <PageContainer enableScroll={false} outerClassName={'p-0 md:p-0'} limitedWidth={false}>
      <ScrollArea
        className="flex-1 h-full"
        viewPortProps={{ ref: viewportRef, className: 'flex flex-col h-full p-2 md:p-8' }}
      >
        <AwaitContainer isLoading={isLoading} className="overflow-hidden flex flex-col h-full">
          <div className="w-full h-full max-w-7xl mx-auto">
            <FormProvider {...form}>
              <Sentinel ref={sentinelRef}>
                {isFetchingNextPage && (
                  <div className="w-full flex justify-center py-6">
                    <Spinner className="size-8" />
                  </div>
                )}
              </Sentinel>
              <div className="flex flex-col gap-4 p-1 pb-3">
                {fields.length === 0 && !isLoading && (
                  <div className="text-center text-muted-foreground py-8">{t('noStopWords')}</div>
                )}
                {fields.map((field, index, array) => (
                  <StopWordItem
                    key={field.id}
                    index={index}
                    onRemove={() => handleRemove(index)}
                    onAdd={handleAdd}
                    onSave={() => handleSave(index)}
                    isLast={index === array.length - 1}
                    canAddNew={allowAddStopWord}
                    isStopWordDirty={isStopWordDirty(index)}
                  />
                ))}
              </div>
              <Sentinel ref={bottomRef} />
            </FormProvider>
          </div>
        </AwaitContainer>
      </ScrollArea>
    </PageContainer>
  );
};
